<template>
    <div ref="refSelf" class="menu-wrapper">
        <component
            ref="parentMenuRef"
            v-bind="innerProps"
            :is="component"
            @close="
                (viaAction: boolean) => {
                    parentClosing = true;
                    $emit('close', viaAction);
                }
            "
        />
    </div>
</template>

<script setup lang="ts">
import {
    type Component,
    nextTick,
    onMounted,
    provide,
    ref,
    useTemplateRef,
    watchEffect,
} from "vue";
import {
    onClickOutside,
    useElementSize,
    useEventListener,
    whenever,
} from "@vueuse/core";
import EventEmitter from "eventemitter3";
import { placeRectBox } from "@/composables/mag-layout-composable";

const props = defineProps<{
    innerProps: Record<string, unknown>;
    parent: HTMLElement | null;
    parentMenu: Component | null;
    parentClosingSignal: boolean | null;
    component: Component;
    dropdown: boolean;
    fromContextMenu?: MouseEvent;
}>();

const ignoreUnpairedClick = ref(props.dropdown ?? false);

const parentMenuRef = useTemplateRef("parentMenuRef");
const parentClosing = ref<boolean>(false);
const closeChildrenEmitter = new EventEmitter();

provide("parentMenu", parentMenuRef);
provide("parentClosing", parentClosing);
provide("closeChildrenEmitter", closeChildrenEmitter);

const refSelf = useTemplateRef("refSelf");

const emit = defineEmits<{
    close: [boolean];
}>();

whenever(
    () => props.parentClosingSignal,
    () => {
        parentClosing.value = true;
        emit("close", false);
    },
);

useEventListener(
    "mouseleave",
    () => {
        ignoreUnpairedClick.value = false;
    },
    {
        once: true,
    },
);

useEventListener(
    "contextmenu",
    (e) => {
        if (
            !e.composedPath().some((el) => el === refSelf.value?.parentElement)
        ) {
            emit("close", false);
        }
    },
    {
        passive: true,
        capture: true,
    },
);

onClickOutside(
    refSelf,
    (e) => {
        if (ignoreUnpairedClick.value) {
            ignoreUnpairedClick.value = false;
            return;
        }

        const parent = refSelf.value?.parentElement;

        if (
            parent &&
            (e.target === parent ||
                parent.compareDocumentPosition(e.target) &
                    Node.DOCUMENT_POSITION_CONTAINED_BY)
        ) {
            return;
        }

        emit("close", false);
    },
    {
        ignore: [".mag-menu"],
    },
);

const { width, height } = useElementSize(() => refSelf.value?.parentElement);

watchEffect(() => {
    if (!props.dropdown || !refSelf.value || !props.parent?.offsetWidth) {
        return;
    }

    refSelf.value.style.minWidth = `${props.parent?.offsetWidth}px`;
});

const placeMenu = () => {
    const el = refSelf.value?.parentElement;
    const parentMenu = props.parentMenu;
    const bttn = props.parent;

    if (!el) {
        return;
    }

    let parentRect: Omit<DOMRectReadOnly, "toJSON">;

    if (props.fromContextMenu) {
        parentRect = {
            bottom: props.fromContextMenu.y,
            height: 0,
            left: props.fromContextMenu.x,
            right: props.fromContextMenu.x,
            top: props.fromContextMenu.y,
            width: 0,
            x: props.fromContextMenu.x,
            y: props.fromContextMenu.y,
        };
    } else if (bttn && "getBoundingClientRect" in bttn) {
        parentRect = bttn.getBoundingClientRect();
    } else {
        return;
    }

    if (props.dropdown && props.parent) {
        el.style.left = parentRect.left + "px";

        if (height.value > window.innerHeight - parentRect.bottom - 8) {
            el.style.transformOrigin = "bottom";
            el.style.top =
                Math.max(0, parentRect.top - height.value - 8) + "px";
        } else {
            el.style.transformOrigin = "top";
            el.style.top = parentRect.bottom + 8 + "px";
        }

        return;
    }

    const placement = placeRectBox({
        anchorBox: parentRect,
        innerBox: {
            x: 0,
            y: 0,
            width: Math.max(width.value, 200),
            height: height.value,
        },
        containingBox: {
            x: 0,
            y: 0,
            width: window.innerWidth,
            height: window.innerHeight,
        },
        inset: 8,
        position: Boolean(bttn) && !Boolean(parentMenu) ? "bottom" : "right",
        align: Boolean(parentMenu) ? "center" : "start",
    });

    el.style.left = placement.left;
    el.style.top = placement.top;
    el.style.transformOrigin = placement.transformOrigin;
};

watchEffect(placeMenu);

onMounted(() => {
    nextTick(placeMenu);
});
</script>

<style scoped lang="scss">
.menu-wrapper {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 6px 0;
    min-width: 200px;
    max-height: calc(
        100vh -
            40px - env(safe-area-inset-bottom, 0px) - env(
                safe-area-inset-top,
                0px
            )
    );
    overflow-y: auto;
}
</style>
